/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ROUTE_PATH } from "../../constants/route";
import Profile from "./Profile";
import PaymentSetup from "./PaymentSetup";
import InventrySetup from "../CreateProfileNew/InventorySetup";
import StyledCreateProfile from "./StyledCreateProfile";
import StepperComponent from "../../components/CommonComponents/Stepper";
import { ADD_VARIENTS_CONSTANTS, BUTTON_LABELS } from "../../constants/lang";
import {
  CreateProfileSchema,
  InventorySetupSchema,
  PaymentSetupSchema,
} from "../../schema/CreateProfileSchem";
import { fetchCreateProfile } from "../../store/reducers/createProfile";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "../../components/CommonComponents/Toast/ToastNotify";
import {
  fetchCreateProduct,
  fetchEditProducts,
  fetchProducts,
  fetchSellerProfile,
} from "../../store/reducers/createProfile/apithunk";
import { updateLoading } from "../../store/reducers/loader";
import rollbar from "../../rollbarConfig";

const CreateProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [isLoading, setisLoading] = useState(false);
  const [prefilledData, setPrefilledData] = useState(null);
  const [products, setProducts] = useState([]);
  const profileData = useSelector((state) => state?.createProfile?.userProfile);
  const [resetShowAddProduct, setResetShowAddProduct] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [showEditProduct, setShowEditProduct] = useState(false);

  const [defaultInventoryType, setDefaultInventoryType] = useState('');
  const existingProducts = useSelector(
    (state) => state?.createProfile?.products?.data
  );

  // Check URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const inventoryType = urlParams.get('inventory-type');
    if (inventoryType === 'shopify') {
      setDefaultInventoryType('shopify');
    } else if (inventoryType === 'manually') {
      setDefaultInventoryType('manually');
    } else {
      setDefaultInventoryType('');
    }
  }, [location.search]);

  const handleResetShowAddProduct = () => {
    setResetShowAddProduct((prev) => !prev);
  };

  const validationSchema = [
    CreateProfileSchema,
    PaymentSetupSchema,
    InventorySetupSchema,
  ];
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setFocus,
    setError,
    clearErrors,
    reset,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema[activeStep]),
  });

  const handleProfileImageChange = (image) => {
    setProfileImage(image);
  };

  const handleInventoryValueChange = (field, value) => {
    setValue(field, value);
  };

  const handleShowAddProductChange = (value) => {
    setShowAddProduct(value);
  };

  const handleShowEditProductChange = (value) => {
    setShowEditProduct(value);
    if (!value) {
      // Reset editProductId when edit form is closed
      setEditProductId(null);
    }
  };

  const steps = [
    <Profile
      control={control}
      errors={errors}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      onProfileImageChange={handleProfileImageChange}
    />,
    <PaymentSetup control={control} errors={errors} />,
    <InventrySetup
      control={control}
      errors={errors}
      onValueChange={handleInventoryValueChange}
      products={products}
      resetShowAddProduct={resetShowAddProduct}
      onShowAddProductChange={handleShowAddProductChange}
      onShowEditProductChange={handleShowEditProductChange}
      setValue={setValue}
      trigger={trigger}
      watch={watch}
      clearErrors={clearErrors}
      setError={setError}
      OnEditAndDelete={() => {
        // Refresh products data after deletion
        const query = `?search=&category=All&page_no=1&page_size=10&seller=${profileData?.id}`;
        dispatch(fetchProducts(query)).then((res) => {
          const { data, status } = res.payload;
          if (status) {
            setProducts(data);
          }
        });
      }}
      onEditSubmit={(productId) => {onSubmit(watch(), true, productId)}}
      SelectedProductId={(id) => {setEditProductId(id)}}
      reset={reset}
      defaultInventoryType={defaultInventoryType}
      showEditProductFromParent={showEditProduct}
    />,
  ];

  const getPrefilledData = async () => {
    dispatch(updateLoading(true));
    try {
      const res = await dispatch(fetchSellerProfile());
      const { data, status, message } = res.payload;

      if (status) {
        if (data?.step === 3) {
          navigate(ROUTE_PATH.PRIVATE_DASHBOARD);
          setActiveStep(2);
        } else {
          setActiveStep(data?.step);
          setPrefilledData(data);
        }
      } else {
        ToastNotifyError(message);
      }
    } catch (err) {
      rollbar.error("Error message", err);
      console.error(err);
    } finally {
      dispatch(updateLoading(false));
    }
  };

  useEffect(() => {
    getPrefilledData();
  }, []);

  useEffect(() => {
    if (prefilledData) {
      setValue("brandName", prefilledData?.brand_name);
      setValue("website", prefilledData?.website_link);
      setValue("instagram", prefilledData?.instagram_link);
      setValue("other", prefilledData?.others_link);
      setValue("brandWriteUp", prefilledData?.brand_bio);
      setValue("productsProcesses", prefilledData?.product_description);
      setValue("favAboutNYC", prefilledData?.fav_about_nyc);
      setValue("bio", prefilledData?.designer_bio);
    }
  }, [prefilledData]);

  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        setFocus(firstErrorField);
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);
      }
    }
  }, [errors, setFocus]);

  const onSubmit = (data, onEdit = false, productId = null) => {
    const isEditMode = onEdit === true;
    const editId = productId || editProductId;
    const step1Data = async () => {
      if (profileImage === null) {
        ToastNotifyError("Profile Picture is Mandatory");
        window.scrollTo(0, 0);
      } else {
        setisLoading(true);
        try {
          const dataToSend = {
            brand_image: profileImage,
            brand_name: data?.brandName,
            website_link: data?.website,
            instagram_link: data?.instagram,
            others_link: data?.other,
            brand_bio: data?.brandWriteUp,
            product_description: data?.productsProcesses,
            fav_about_nyc: data?.favAboutNYC,
            designer_bio: data?.bio,
            tags: selectedTags.map((tag) => tag.id),
            step: 1,
          };

          const res = await dispatch(fetchCreateProfile(dataToSend));

          if (res?.payload?.status === true) {
            if (activeStep < steps?.length - 1) {
              setActiveStep((prev) => prev + 1);
            }
          } else {
            console.error("API error: ", res?.payload?.message);
          }
        } catch (err) {
          rollbar.error("Error message", err); 
          console.error(err);
        } finally {
          setisLoading(false);
        }
      }
    };
    const step2Data = async () => {
      setisLoading(true);
      try {
        const dataToSend = { step: "2" };
        const res = await dispatch(fetchCreateProfile(dataToSend));
        const { status, message } = res?.payload || {};
        if (status) {
          setActiveStep((prev) => prev + 1);
        } else {
          ToastNotifyError(message);
        }
      } catch (err) {
        rollbar.error("Error message", err);
        console.error(err);
      } finally {
        setisLoading(false);
      }
    };
    const step3Data = async () => {
      setisLoading(true);
      const totalVariantsQuantity = watch("variants")
        ?.map((element) => Number(element.varientQuantity))
        ?.reduce((acc, quantity) => acc + quantity);
      const totalQuantity = Number(data.totalQuantity);

      if (totalQuantity !== totalVariantsQuantity) {
        setError("totalQuantity", {
          type: "manual",
          message: ADD_VARIENTS_CONSTANTS.errorMessageTotalQuantity,
        });
        setisLoading(false);
        return;
      }

      const variants = watch("variants");
      const hasDuplicate = variants?.some((variant, index) => {
        return (
          variants?.findIndex(
            (v, i) =>
              i !== index &&
              v.selectSize === variant.selectSize &&
              v.selectColor.value === variant.selectColor.value
          ) !== -1
        );
      });

      if (hasDuplicate) {
        ToastNotifyError("Item already exists with the same size and color");
        setisLoading(false);
        return;
      }

      try {
        const dataToSend = {
          name: data.productName,
          description: data.description,
          categories: data.Categories.map((element) => element.id),
          status: data.status === "Active" ? "A" : "In",
          is_featured: true,
          tags: data.tags.map((element) => element.id),
          price: data.priceinUSD,
          quantity: data.totalQuantity,
          step: "3",
          variant_data: data.variants.map((element) => ({
            size_obj: element?.selectSize?.id || element?.selectSize,
            quantity: element?.varientQuantity,
            color_obj: element?.selectColor?.id || element?.selectColor,
            variant_img: element?.selectImage,
          })),
        };

        if (isEditMode && !editId) {
          ToastNotifyError("Product ID is missing. Please try again.");
          setisLoading(false);
          return;
        }
        
        const EditDataToSend = {
          id: editId,
          dataToSend,
        }
       

        const res =  isEditMode ? await dispatch(fetchEditProducts(EditDataToSend)) : await dispatch(fetchCreateProduct(dataToSend));
        const { status, message } = res?.payload || {};
        if (status) {
          ToastNotifySuccess(message);
          reset();
          handleResetShowAddProduct();
          handleShowEditProductChange(false);
          const query = `?search=&category=All&page_no=1&page_size=10&seller=${profileData?.id}`;
          try {
            const res = await dispatch(fetchProducts(query));
            const { data, status, message } = res.payload;
            if (status) {
              setProducts(data);
            } else {
              ToastNotifyError(message);
            }
          } catch {
            rollbar.error("Error message", res?.payload?.message);
            console.error("API error: ", res?.payload?.message);
          }
        } else {
          console.error("API error: ", res?.payload?.message);
        }
      } catch (err) {
        console.error(err);
        rollbar.error("Error message", err);
      } finally {
        setisLoading(false);
      }
    };

    if (activeStep === 0) {
      step1Data();
    } else if (activeStep === 1) {
      step2Data();
    } else {
      step3Data();
    }
  };

  const finalSubmission = async () => {
    try {
      const dataToSend = { step: "3" };
      const res = await dispatch(fetchCreateProfile(dataToSend));
      const { status, message } = res?.payload || {};
      if (status) {
        ToastNotifySuccess(message);
        navigate(ROUTE_PATH.PRIVATE_DASHBOARD);
      } else {
        ToastNotifyError(message);
      }
    } catch (err) {
      console.error(err);
      rollbar.error("Error message", err);
    } finally {
      setisLoading(false);
    }
  }

  return (
    <StyledCreateProfile>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        className='create-profile-wrap'
      >
        <StepperComponent
          steps={["Create Profile", "Payment Setup", "Inventory Setup"]}
          className='stepper'
          activeStep={activeStep}
        />
        <Box>{steps[activeStep]}</Box>
        <Box className='btn-wrap'>
          {activeStep === 2 && defaultInventoryType !== 'shopify' ? (
            !showAddProduct &&  !showEditProduct && existingProducts?.length > 0 ? (
              <Button
                variant='contained'
                color='primary'
                isLoading={isLoading}
                aria-label='submit-btn'
                className='submit-btn'
                onClick={() => finalSubmission()}
              >
                {BUTTON_LABELS.SAVE_AND_CONTINUE}
              </Button>
            ) : null
          ) : activeStep === 1 ? (
            profileData.stripe_connect_details !== null ? (
              <Button
                type='submit'
                variant='contained'
                color='primary'
                isLoading={isLoading}
                aria-label='submit-btn'
                className='submit-btn'
              >
                {BUTTON_LABELS.SAVE_AND_CONTINUE}
              </Button>
            ) : null
          ) : defaultInventoryType !== 'shopify' ? (
            <Button
              type='submit'
              variant='contained'
              color='primary'
              isLoading={isLoading}
              aria-label='submit-btn'
              className='submit-btn'
            >
              {BUTTON_LABELS.SAVE_AND_CONTINUE}
            </Button>
          ) : (
            ""
          )}
          {!showAddProduct && !showEditProduct && activeStep !== 0 ? (
            <Link
              className='go-back-btn'
              onClick={() => {
                // Remove inventory-type parameter from URL when going back
                const urlParams = new URLSearchParams(location.search);
                urlParams.delete('inventory-type');
                const newSearch = urlParams.toString();
                const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
                navigate(newPath, { replace: true });
                
                // Update active step
                setActiveStep((prev) => Math.max(prev - 1, 0));
              }}
            >
              Go Back
            </Link>
          ) : (
            ""
          )}
        </Box>
      </Box>
    </StyledCreateProfile>
  );
};

export default CreateProfile;
